import { Shield } from "lucide-react";
import { Container, PillButton, SmartImg } from "@/components/common/ui";
import { ABOUT_LETTER, IMAGES, PEOPLE, SITE, STATS, TEAM_PHOTO } from "@/lib/site";
import { TESTIMONIALS } from "@/lib/testimonials";
import { AgentDirectory } from "@/components/team/AgentDirectory";

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      <section className="border-b border-neutral-200 py-14 sm:py-20">
        <Container>
          <p className="eyebrow">{SITE.companies.join(" · ")}</p>
          <h1 className="mt-4 max-w-4xl font-display text-5xl font-normal leading-[1.04] sm:text-6xl">The Rob Resh Team</h1>
          <p className="mt-5 max-w-2xl text-[18px] text-neutral-600">“Setting the bar high for superior customer service and unrestricted access to property information throughout Greater Hampton Roads & Greater Richmond.”</p>
        </Container>
      </section>

      <section className="py-16 sm:py-20">
        <Container className="grid gap-12 lg:grid-cols-[1.5fr_1fr]">
          <div className="space-y-5 text-[17px] leading-relaxed text-neutral-700" data-testid="about-letter">
            {ABOUT_LETTER.map((p) => <p key={p.slice(0, 24)}>{p}</p>)}
          </div>
          <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
            <SmartImg src={TEAM_PHOTO} fallbackSrc={IMAGES.militaryFamily} alt="The Rob Resh Team" className="aspect-[4/3] w-full rounded-md object-cover" />
            <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-md border border-neutral-200 bg-neutral-200">
              {STATS.map((s) => (
                <div key={s.id} className="bg-white p-5"><dt className="font-display text-3xl">{s.value}</dt><dd className="mt-1 text-[13px] text-neutral-600">{s.label}</dd></div>
              ))}
            </dl>
            <PillButton href={SITE.phoneMainHref} className="w-full" testId="about-call">Call {SITE.phoneMain}</PillButton>
          </aside>
        </Container>
      </section>

      <section className="bg-mist py-16 sm:py-20">
        <Container>
          <h2 className="text-3xl font-normal sm:text-4xl">Search our agent directory</h2>
          <p className="mt-2 text-[16px] text-neutral-600">Setting the standard for excellence in the Virginia Beach & Greater Richmond area real estate community.</p>
          <div className="mt-10"><AgentDirectory fallback={PEOPLE} /></div>
        </Container>
      </section>

      <section id="testimonials" className="scroll-mt-20 py-16 sm:py-20">
        <Container>
          <h2 className="text-3xl font-normal sm:text-4xl">Testimonials</h2>
          <p className="mt-2 text-[16px] text-neutral-600">What clients say about working with the 757 Realty team.</p>
          <div className="mt-10 columns-1 gap-5 md:columns-2 xl:columns-3" data-testid="testimonial-wall">
            {TESTIMONIALS.map((t) => (
              <figure key={t.id} className="mb-5 break-inside-avoid rounded-md border border-neutral-200 p-6" data-testid={`testimonial-${t.id}`}>
                <blockquote className="text-[15px] leading-relaxed text-neutral-700">“{t.text}”</blockquote>
                <figcaption className="mt-4 flex items-center gap-2 text-[14px] font-medium text-ink">
                  {t.name}
                  {t.military && <span className="inline-flex items-center gap-1 rounded-full bg-ink px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-brass"><Shield className="h-2.5 w-2.5" /> Military</span>}
                </figcaption>
              </figure>
            ))}
          </div>
        </Container>
      </section>

      <section className="pb-20">
        <Container>
          <div className="flex flex-col items-start justify-between gap-6 rounded-md bg-ink p-8 text-white sm:flex-row sm:items-center sm:p-10">
            <div>
              <p className="font-display text-3xl">Interested in a career in real estate?</p>
              <p className="mt-2 text-white/75">Learn why The Rob Resh Team is the right choice for career-minded agents.</p>
            </div>
            <PillButton to="/contact?kind=career" tone="light" testId="about-careers">Talk to Rob</PillButton>
          </div>
        </Container>
      </section>
    </div>
  );
}
